/**
 * Pure helpers for Settings ▸ Model policy ▸ Bench results: reading an
 * uploaded results file and describing what importing it would do. The server
 * validates every entry again — this only catches the wrong file early, so the
 * admin is not shown a preview of a tax return.
 */
import type { BenchEntryStatus, BenchPreview, BenchResultsFile } from '../types';

export const BENCH_FORMAT = 'stage-bench/1';

/** The file's text as a results file, or the reason it is not one. */
export function readBenchFile(text: string): { file: BenchResultsFile } | { error: string } {
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    return { error: 'That file is not JSON.' };
  }
  const obj = data as Partial<BenchResultsFile> | null;
  if (!obj || typeof obj !== 'object' || obj.format !== BENCH_FORMAT) {
    return { error: `Not a bench results file (expected format ${BENCH_FORMAT}).` };
  }
  if (!Array.isArray(obj.entries)) return { error: 'The file has no entries.' };
  return { file: obj as BenchResultsFile };
}

const WORDS: Record<BenchEntryStatus, string> = {
  new: 'new',
  replaces: 'newer than what is here',
  older: 'older than what is here (kept, not shown)',
  duplicate: 'already here',
  invalid: 'invalid (skipped)',
};

const ORDER: BenchEntryStatus[] = ['new', 'replaces', 'older', 'duplicate', 'invalid'];

/** "3 new · 1 newer than what is here · 2 already here". */
export function previewSummary(counts: BenchPreview['counts']): string {
  const parts = ORDER.filter((s) => counts[s]).map((s) => `${counts[s]} ${WORDS[s]}`);
  return parts.length ? parts.join(' · ') : 'The file has no results.';
}

/** Whether Import would write anything at all. */
export function importWouldWrite(counts: BenchPreview['counts']): boolean {
  return Boolean(counts.new || counts.replaces || counts.older);
}

/** `bench-results-<host>-2026-09-13.json`, so downloads from two servers don't collide. */
export function downloadName(host: string, now: Date): string {
  const safeHost = host.replace(/[^a-z0-9.-]/gi, '') || 'server';
  return `bench-results-${safeHost}-${now.toISOString().slice(0, 10)}.json`;
}
